/* ==========================================================================
   ShopyGuru — theme.js (global, deferred)
   Cart (AJAX add / notification / cart page), predictive search, search scope,
   carousels, quantity steppers, facets, recommendations, recently viewed, share.
   No third-party dependencies.
   ========================================================================== */
(() => {
  'use strict';

  const T = window.theme || { routes: {}, strings: {} };
  const R = Object.assign({ root: '/', cart: '/cart', checkout: '/checkout', cartAdd: '/cart/add', cartChange: '/cart/change', search: '/search', predictiveSearch: '/search/suggest', recommendations: '/recommendations/products' }, T.routes);
  const S = T.strings || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const debounce = (fn, wait) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const announce = (message) => {
    const region = document.getElementById('A11yLiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };
  const sizedImage = (src, width) => {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      return src;
    }
  };

  /* ------------------------------------------------------------------
     Sticky offset (header height) for sticky galleries/sidebars
     ------------------------------------------------------------------ */
  const headerSection = document.querySelector('.section-header');
  const setStickyOffset = () => {
    if (!headerSection) return;
    const sticky = window.getComputedStyle(headerSection).position === 'sticky';
    const h = sticky ? headerSection.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--sticky-offset', `${Math.round(h + 12)}px`);
  };
  setStickyOffset();
  if (headerSection && 'ResizeObserver' in window) new ResizeObserver(setStickyOffset).observe(headerSection);

  /* ------------------------------------------------------------------
     Cart API
     ------------------------------------------------------------------ */
  const errorMessage = (data) => (data && (data.description || data.message)) || S.cartError;

  const Cart = {
    async add(formData) {
      const res = await fetch(`${R.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      let data = {};
      try { data = await res.json(); } catch (e) { /* non-JSON error */ }
      if (!res.ok || data.status) throw new Error(errorMessage(data));
      return data;
    },
    async get() {
      const res = await fetch(`${R.cart}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(S.cartError);
      return res.json();
    },
    async change(line, quantity, sections) {
      const res = await fetch(`${R.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections, sections_url: window.location.pathname })
      });
      let data = {};
      try { data = await res.json(); } catch (e) { /* ignore */ }
      if (!res.ok || data.status) throw new Error(errorMessage(data));
      return data;
    },
    async updateNote(note) {
      await fetch(`${R.cart}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
    }
  };

  const updateCartCount = (count) => {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; });
    const label = (count === 1 ? S.cartCountOne : S.cartCountOther) || '';
    $$('[data-cart-count-label]').forEach((el) => { el.textContent = label.replace('[count]', count); });
    const bubble = $('.site-header__cart-count');
    if (bubble && !reduceMotion()) {
      bubble.classList.remove('is-bumped');
      void bubble.offsetWidth;
      bubble.classList.add('is-bumped');
    }
  };

  /* ------------------------------------------------------------------
     Cart notification (only after a confirmed successful add)
     ------------------------------------------------------------------ */
  const Notification = {
    el: null,
    lastFocus: null,
    init() {
      this.el = $('[data-cart-notification]');
      if (!this.el) return;
      const close = $('[data-cart-notification-close]', this.el);
      if (close) close.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.el.hidden) this.close();
      });
      document.addEventListener('click', (e) => {
        if (this.el.hidden) return;
        if (this.el.contains(e.target) || e.target.closest('form[data-ajax-cart], [data-sticky-atc]')) return;
        this.close();
      });
    },
    open(item, quantity) {
      if (!this.el) return;
      const box = $('[data-cart-notification-product]', this.el);
      box.textContent = '';
      if (item.image) {
        const img = document.createElement('img');
        img.className = 'cart-notification__img';
        img.src = sizedImage(item.image, 160);
        img.alt = '';
        img.width = 64;
        img.height = 64;
        box.appendChild(img);
      }
      const info = document.createElement('div');
      const name = document.createElement('p');
      name.className = 'cart-notification__name';
      name.textContent = item.product_title || item.title || '';
      info.appendChild(name);
      const meta = [];
      if (item.variant_title && !item.product_has_only_default_variant) meta.push(item.variant_title);
      meta.push(`${S.quantity || 'Quantity'}: ${quantity}`);
      const metaEl = document.createElement('p');
      metaEl.className = 'cart-notification__meta';
      metaEl.textContent = meta.join(' · ');
      info.appendChild(metaEl);
      box.appendChild(info);
      this.lastFocus = document.activeElement;
      this.el.hidden = false;
      this.el.focus({ preventScroll: true });
    },
    close() {
      if (!this.el || this.el.hidden) return;
      this.el.hidden = true;
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
    }
  };
  Notification.init();

  /* ------------------------------------------------------------------
     Checkout buttons that appear once the cart has items
     ------------------------------------------------------------------ */
  const revealCheckout = (count) => {
    $$('[data-buybox-checkout]').forEach((el) => { el.hidden = !(count > 0); });
  };

  /* ------------------------------------------------------------------
     AJAX add to cart for any form[data-ajax-cart]
     - Add to Cart: adds, then shows the confirmation popup.
     - Order Now ([data-order-now]): sets this variant to exactly the selected
       quantity in the cart, then goes straight to checkout.
     ------------------------------------------------------------------ */
  const orderNow = async (formData, quantity) => {
    const variantId = String(formData.get('id'));
    const cart = await Cart.get().catch(() => null);
    const index = cart ? cart.items.findIndex((item) => String(item.variant_id) === variantId) : -1;
    if (index > -1) await Cart.change(index + 1, quantity);
    else await Cart.add(formData);
    window.location.assign(R.checkout);
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-cart]');
    if (!form) return;
    const isOrderNow = Boolean(e.submitter && e.submitter.hasAttribute('data-order-now'));
    // cart_type "page": Add to Cart uses the native POST (Shopify redirects to /cart)
    if (T.cartType === 'page' && !isOrderNow) return;
    e.preventDefault();

    const buttons = $$('[type="submit"]', form);
    const formId = form.getAttribute('id'); // form.id would return the <select name="id"> inside the form
    if (formId) buttons.push(...$$(`[type="submit"][form="${formId}"]`));
    if (buttons.some((b) => b.getAttribute('aria-busy') === 'true')) return;

    const section = form.closest('[data-product-section]');
    const errorEl = $('[data-form-error]', form) || (section && $('[data-form-error]', section));
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

    buttons.forEach((b) => { b.disabled = true; });
    const busy = isOrderNow ? buttons.filter((b) => b.hasAttribute('data-order-now')) : buttons.filter((b) => !b.hasAttribute('data-order-now'));
    busy.forEach((b) => b.setAttribute('aria-busy', 'true'));
    const formData = new FormData(form);
    const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;
    let leaving = false;

    try {
      if (isOrderNow) {
        await orderNow(formData, quantity);
        leaving = true;
        return;
      }
      const item = await Cart.add(formData);
      const cart = await Cart.get().catch(() => null);
      if (cart) {
        updateCartCount(cart.item_count);
        revealCheckout(cart.item_count);
      } else {
        revealCheckout(1);
      }
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { item, cart } }));
      announce(S.addedToCart);
      Notification.open(item, quantity);
    } catch (err) {
      const message = err && err.message ? err.message : S.cartError;
      if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
      announce(message);
    } finally {
      // Keep the Order Now spinner while the browser navigates to checkout
      if (!leaving) {
        buttons.forEach((b) => {
          b.removeAttribute('aria-busy');
          b.disabled = b.dataset.unavailable === 'true';
        });
      }
    }
  });

  // Pages restored from the back/forward cache: refresh counts and button states
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    $$('[aria-busy="true"]').forEach((b) => {
      b.removeAttribute('aria-busy');
      b.disabled = b.dataset.unavailable === 'true';
    });
    Cart.get().then((cart) => {
      updateCartCount(cart.item_count);
      revealCheckout(cart.item_count);
    }).catch(() => {});
  });

  /* ------------------------------------------------------------------
     Quantity steppers
     ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quantity-minus], [data-quantity-plus]');
    if (!btn) return;
    const input = btn.closest('[data-quantity]') && btn.closest('[data-quantity]').querySelector('input');
    if (!input) return;
    const step = parseFloat(input.step) || 1;
    const min = input.min !== '' ? parseFloat(input.min) : 0;
    const max = input.max !== '' ? parseFloat(input.max) : Infinity;
    let value = parseFloat(input.value) || 0;
    value = btn.hasAttribute('data-quantity-plus') ? value + step : value - step;
    value = Math.min(Math.max(value, min), max);
    if (String(value) !== input.value) {
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ------------------------------------------------------------------
     Carousels (CSS scroll-snap + arrow buttons)
     ------------------------------------------------------------------ */
  const initCarousel = (root) => {
    if (!root || root.dataset.carouselReady) return;
    root.dataset.carouselReady = 'true';
    const track = $('[data-carousel-track]', root);
    const prev = $('[data-carousel-prev]', root);
    const next = $('[data-carousel-next]', root);
    if (!track) return;
    // In RTL, scrollLeft runs from 0 to negative and "next" scrolls left
    const rtl = getComputedStyle(track).direction === 'rtl';
    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      const pos = Math.abs(track.scrollLeft);
      if (prev) prev.disabled = pos <= 2;
      if (next) next.disabled = pos >= max;
    };
    const page = (dir) => track.scrollBy({ left: (rtl ? -dir : dir) * track.clientWidth * 0.9, behavior: reduceMotion() ? 'auto' : 'smooth' });
    if (prev) prev.addEventListener('click', () => page(-1));
    if (next) next.addEventListener('click', () => page(1));
    track.addEventListener('scroll', debounce(update, 60), { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    update();
  };
  $$('[data-carousel]').forEach(initCarousel);

  /* ------------------------------------------------------------------
     Header search: product-type scope + predictive search combobox
     ------------------------------------------------------------------ */
  const initPredictive = (form) => {
    const input = $('[data-predictive-input]', form);
    const results = $('[data-predictive-results]', form);
    const status = $('[data-predictive-status]', form);
    if (!input || !results) return;
    let controller;
    let activeIndex = -1;
    const options = () => $$('[role="option"]', results);

    const close = () => {
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    };
    const open = () => {
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };
    const setActive = (index) => {
      const opts = options();
      if (!opts.length) return;
      activeIndex = (index + opts.length) % opts.length;
      opts.forEach((o, i) => o.setAttribute('aria-selected', i === activeIndex ? 'true' : 'false'));
      input.setAttribute('aria-activedescendant', opts[activeIndex].id);
      opts[activeIndex].scrollIntoView({ block: 'nearest' });
    };

    const fetchResults = async (term) => {
      if (controller) controller.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,query,page',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search'
      });
      try {
        const res = await fetch(`${R.predictiveSearch}?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) throw new Error(String(res.status));
        const doc = parseHTML(await res.text());
        const inner = $('.predictive-search__inner', doc);
        if (!inner) { close(); return; }
        results.innerHTML = '';
        results.appendChild(document.importNode(inner, true));
        activeIndex = -1;
        open();
        const count = options().length;
        if (status) status.textContent = count ? (S.suggestionsAvailable || '').replace('[count]', count) : (S.searchNoResults || '');
      } catch (err) {
        if (err.name !== 'AbortError') close();
      }
    };
    const onInput = debounce(() => {
      const term = input.value.trim();
      if (term.length < 2) { close(); return; }
      fetchResults(term);
    }, 220);

    input.addEventListener('input', onInput);
    input.addEventListener('keydown', (e) => {
      if (results.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'Enter' && activeIndex > -1) {
        const link = $('a', options()[activeIndex]);
        if (link) { e.preventDefault(); window.location.href = link.href; }
      }
    });
    input.addEventListener('focus', () => {
      if (results.childElementCount && input.value.trim().length >= 2) open();
    });
    form.addEventListener('focusout', (e) => {
      if (e.relatedTarget && !form.contains(e.relatedTarget)) close();
    });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) close(); });
  };

  $$('[data-search-form]').forEach((form) => {
    form.addEventListener('submit', (e) => {
      const input = $('[data-search-input]', form);
      const scope = $('[data-search-scope]', form);
      if (!input) return;
      const term = input.value.trim();
      const scopeValue = scope ? scope.value.replace(/"/g, '').trim() : '';
      if (!scopeValue) {
        if (!term) { e.preventDefault(); input.focus(); }
        return;
      }
      e.preventDefault();
      const params = new URLSearchParams(new FormData(form));
      params.set('q', `${term} product_type:"${scopeValue}"`.trim());
      window.location.href = `${R.search}?${params.toString()}`;
    });
    initPredictive(form);
  });

  /* ------------------------------------------------------------------
     Product recommendations (lazy, Section Rendering)
     ------------------------------------------------------------------ */
  const loadRecommendations = async (section) => {
    const url = section.dataset.url;
    if (!url || section.dataset.loaded) return;
    section.dataset.loaded = 'true';
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const incoming = $('[data-recommendations]', parseHTML(await res.text()));
      if (incoming && incoming.innerHTML.trim()) {
        section.innerHTML = incoming.innerHTML;
        section.hidden = false;
        $$('[data-carousel]', section).forEach(initCarousel);
      }
    } catch (e) { /* stay hidden */ }
  };
  const recSections = $$('[data-recommendations]');
  if (recSections.length && 'IntersectionObserver' in window) {
    // The section is hidden until filled, so observe its (always rendered) Shopify wrapper.
    const targets = new Map();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        loadRecommendations(targets.get(entry.target));
      });
    }, { rootMargin: '0px 0px 800px 0px' });
    recSections.forEach((s) => {
      const target = s.parentElement || s;
      targets.set(target, s);
      io.observe(target);
    });
  } else {
    recSections.forEach(loadRecommendations);
  }

  /* ------------------------------------------------------------------
     Recently viewed (stored in the shopper's browser only)
     ------------------------------------------------------------------ */
  const RV_KEY = 'shopyguru:recently-viewed';
  const readRecent = () => {
    try { return JSON.parse(window.localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; }
  };
  const writeRecent = (list) => {
    try { window.localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, 16))); } catch (e) { /* storage unavailable */ }
  };
  const productSection = $('[data-product-section]');
  if (productSection && productSection.dataset.productHandle) {
    const handle = productSection.dataset.productHandle;
    writeRecent([handle, ...readRecent().filter((h) => h !== handle)]);
  }
  $$('[data-recently-viewed]').forEach(async (section) => {
    const limit = parseInt(section.dataset.limit, 10) || 8;
    const current = section.dataset.current;
    const handles = readRecent().filter((h) => h && h !== current).slice(0, limit);
    if (!handles.length) return;
    const list = $('[data-recently-viewed-list]', section);
    const pages = await Promise.all(handles.map((h) =>
      fetch(`${R.root}products/${encodeURIComponent(h)}?section_id=card-product`)
        .then((r) => (r.ok ? r.text() : ''))
        .catch(() => '')
    ));
    const fragment = document.createDocumentFragment();
    const stillValid = [];
    pages.forEach((html, i) => {
      if (!html) return;
      const card = $('.card-product', parseHTML(html));
      if (!card) return;
      stillValid.push(handles[i]);
      const li = document.createElement('li');
      li.className = 'carousel__slide';
      li.appendChild(document.importNode(card, true));
      fragment.appendChild(li);
    });
    if (!stillValid.length) return;
    list.appendChild(fragment);
    section.hidden = false;
    initCarousel($('[data-carousel]', section));
  });

  /* ------------------------------------------------------------------
     Share & back to top
     ------------------------------------------------------------------ */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (btn) {
      const url = btn.dataset.shareUrl || window.location.href;
      const title = btn.dataset.shareTitle || document.title;
      const status = btn.parentElement && $('[data-share-status]', btn.parentElement);
      if (navigator.share) {
        try { await navigator.share({ title, url }); } catch (err) { /* cancelled */ }
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        if (status) {
          status.textContent = S.shareCopied;
          window.setTimeout(() => { status.textContent = ''; }, 3000);
        }
      } catch (err) { /* clipboard unavailable */ }
      return;
    }
    const top = e.target.closest('[data-back-to-top]');
    if (top) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: reduceMotion() ? 'auto' : 'smooth' });
      const main = document.getElementById('MainContent');
      if (main) main.focus({ preventScroll: true });
    }
  });

  window.ShopyGuru = { Cart, updateCartCount, announce, debounce, parseHTML, initCarousel, strings: S, routes: R };

  /* ------------------------------------------------------------------
     Theme editor support
     ------------------------------------------------------------------ */
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-carousel]', e.target).forEach(initCarousel);
    $$('[data-recommendations]', e.target).forEach(loadRecommendations);
    setStickyOffset();
  });
})();
